import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Wand2, CheckCircle2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api, { formatPaise, formatError } from '@/lib/api';
import { goalLabel } from '@/lib/campaign';

const STATUS = {
  draft: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  approved: 'bg-money-soft text-money border-money/40',
  retired: 'bg-zinc-500/10 text-muted-foreground border-zinc-500/30',
};
const toLines = (lessons) => (lessons || []).map(l => l.text).join('\n');
const fromLines = (text) => text.split('\n').map(t => t.trim()).filter(Boolean);

/** Admin tab: how much the system has learned (the ad ledger, benchmarks, prompt versions) and its playbooks,
 * which the analyst drafts weekly and an admin edits and approves before the ad writer follows them. */
export default function AdminLearning() {
  const [overview, setOverview] = useState(null);
  const [books, setBooks] = useState({ playbooks: [], types: [], goals: [] });
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(null); // {id, text}
  const [form, setForm] = useState({ business_type: '', goal: '', text: '' });

  const load = useCallback(() => {
    api.get('/api/admin/learning').then(({ data }) => setOverview(data)).catch(() => {});
    api.get('/api/admin/playbooks').then(({ data }) => setBooks(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, done) => {
    setBusy(key);
    try {
      await fn();
      if (done) toast.success(done);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Something went wrong');
    }
    setBusy('');
  };
  const typeLabel = (slug) => (books.types.find(t => t.slug === slug) || {}).label || slug;
  const create = () => act('create', async () => {
    await api.post('/api/admin/playbooks', { business_type: form.business_type, goal: form.goal, lessons: fromLines(form.text) });
    setForm({ business_type: '', goal: '', text: '' });
  }, 'Draft saved');

  const l = overview?.ledger;
  return (
    <div className="space-y-4" data-testid="admin-learning">
      {l && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          {[['Ads recorded', l.ads], ['With results', l.settled], ['Ads with a winning text', l.tests_decided],
            ['Owner feedback', l.feedback], ['Said real customers', l.good_feedback]].map(([label, n]) => (
            <div key={label} className="p-3 rounded-xl bg-card border-2 border-ink">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
              <p className="text-xl font-bold font-mono">{n}</p>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Playbooks</p>
            <p className="text-xs text-muted-foreground">The analyst drafts these every week from finished ads. The ad writer follows a playbook only once you approve it.</p>
          </div>
          <Button size="sm" variant="outline" disabled={busy === 'analyst'}
            onClick={() => act('analyst', () => api.post('/api/admin/playbooks/run-analyst'), 'The analyst has finished')}>
            {busy === 'analyst' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />} Run the analyst now
          </Button>
        </div>
        {books.playbooks.length === 0 && <p className="text-xs text-muted-foreground">No playbooks yet. The analyst needs 5 finished ads for a type of business and goal, or write one below.</p>}
        {books.playbooks.map(p => (
          <div key={p.id} className="p-3 rounded-lg border-2 border-ink/15 space-y-2" data-testid={`playbook-${p.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold">{typeLabel(p.business_type)} · {goalLabel(p.goal)}</p>
              <Badge variant="outline" className="text-[10px]">v{p.version}</Badge>
              <Badge variant="outline" className={`text-[10px] ${STATUS[p.status]}`}>{p.status}</Badge>
              <span className="text-[11px] text-muted-foreground">{p.source === 'analyst' ? `by the analyst, from ${p.ads} ads` : 'written by hand'}</span>
            </div>
            {editing?.id === p.id ? (
              <>
                <Textarea rows={5} value={editing.text} onChange={e => setEditing({ ...editing, text: e.target.value })} aria-label="Lessons, one per line" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="gradient-bg" disabled={busy === p.id}
                    onClick={() => act(p.id, async () => { await api.put(`/api/admin/playbooks/${p.id}`, { lessons: fromLines(editing.text) }); setEditing(null); }, 'Saved')}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </>
            ) : (
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                {p.lessons.map((x, i) => <li key={i}>{x.text}{x.ads ? <span className="text-muted-foreground"> ({x.ads} ads)</span> : null}</li>)}
              </ul>
            )}
            {editing?.id !== p.id && (
              <div className="flex flex-wrap gap-2">
                {p.status === 'draft' && (
                  <>
                    <Button size="sm" className="gradient-bg" disabled={busy === p.id}
                      onClick={() => act(p.id, () => api.post(`/api/admin/playbooks/${p.id}/approve`), 'Approved: new ads follow it')}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ id: p.id, text: toLines(p.lessons) })}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  </>
                )}
                {p.status !== 'retired' && (
                  <Button size="sm" variant="ghost" disabled={busy === p.id}
                    onClick={() => act(p.id, () => api.post(`/api/admin/playbooks/${p.id}/retire`), 'Retired')}>Retire</Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="p-4 rounded-xl bg-card border-2 border-ink space-y-3">
        <p className="text-sm font-semibold">Write a playbook</p>
        <p className="text-xs text-muted-foreground">Start from what you already know works. It's saved as a draft for you to approve.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="pb-type" className="text-xs">Type of business</Label>
            <select id="pb-type" className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}>
              <option value="">Choose…</option>
              {books.types.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="pb-goal" className="text-xs">Goal</Label>
            <select id="pb-goal" className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })}>
              <option value="">Choose…</option>
              {books.goals.map(g => <option key={g} value={g}>{goalLabel(g)}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label htmlFor="pb-text" className="text-xs">Lessons, one per line</Label>
          <Textarea id="pb-text" rows={4} className="mt-1" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })}
            placeholder={'Put the price in the first line.\nUse a real photo of the kitchen.'} />
        </div>
        <Button size="sm" className="gradient-bg" disabled={busy === 'create' || !form.business_type || !form.goal || !fromLines(form.text).length} onClick={create}>
          {busy === 'create' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Save draft
        </Button>
      </div>

      {overview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-card border-2 border-ink min-w-0">
            <p className="text-sm font-semibold mb-1">Benchmarks</p>
            <p className="text-xs text-muted-foreground mb-2">Typical cost per result, refreshed daily (needs 3 finished ads).</p>
            {overview.benchmarks.length === 0 ? <p className="text-xs text-muted-foreground">Not enough finished ads yet.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[320px] text-xs">
                  <thead><tr className="text-left text-muted-foreground"><th className="font-medium py-1">Business</th><th className="font-medium">Goal</th><th className="font-medium">City</th><th className="font-medium text-right">Per result</th><th className="font-medium text-right">Ads</th></tr></thead>
                  <tbody>
                    {overview.benchmarks.map((b, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-1">{b.business_type === '*' ? 'All' : typeLabel(b.business_type)}</td><td>{goalLabel(b.goal)}</td>
                        <td>{b.city || 'Any'}</td><td className="text-right font-mono">{formatPaise(b.cpr_paise)}</td><td className="text-right font-mono">{b.ads}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="p-4 rounded-xl bg-card border-2 border-ink min-w-0">
            <p className="text-sm font-semibold mb-1">Prompt versions</p>
            <p className="text-xs text-muted-foreground mb-2">Typical cost per result of ads made with each version of the AI's instructions.</p>
            {overview.prompt_versions.length === 0 ? <p className="text-xs text-muted-foreground">No finished ads yet.</p> : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="font-medium py-1">Version</th><th className="font-medium text-right">Per result</th><th className="font-medium text-right">Ads</th></tr></thead>
                <tbody>
                  {overview.prompt_versions.map(v => (
                    <tr key={v.version} className="border-t border-border"><td className="py-1 font-mono">{v.version}</td><td className="text-right font-mono">{formatPaise(v.cpr_paise)}</td><td className="text-right font-mono">{v.ads}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              Past ads read: {overview.past_ads.done} businesses{overview.past_ads.no_permission ? `, ${overview.past_ads.no_permission} didn't allow it` : ''}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
